import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheck, 
  faExclamationTriangle,
  faSpinner,
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { publicAPI } from '../../services/api';
import { ConsentForm } from './ConsentForm';
import './Auth.css';

export const AccountActivation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlToken = searchParams.get('token');

  // States for different activation modes
  const [activationMode, setActivationMode] = useState<'url' | 'manual'>(!urlToken ? 'manual' : 'url');
  const [manualToken, setManualToken] = useState('');
  const [step, setStep] = useState<'token' | 'consent' | 'password'>(!urlToken ? 'token' : 'consent');
  const [consentData, setConsentData] = useState<any>(null);
  const [userData, setUserData] = useState<{full_name: string, email: string} | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showPassword, setShowPassword] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(!!urlToken);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Security/Rate limiting states
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lastAttemptTime, setLastAttemptTime] = useState<Date | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockUntil, setBlockUntil] = useState<Date | null>(null);
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
  });

  const validateToken = async (tokenToValidate: string) => {
    // Check if we're currently blocked
    if (isBlocked && blockUntil && new Date() < blockUntil) {
      const remainingTime = Math.ceil((blockUntil.getTime() - new Date().getTime()) / 1000);
      setError(`Zbyt wiele nieudanych prób. Spróbuj ponownie za ${remainingTime} sekund.`);
      return;
    }

    // Clear block if time has passed
    if (isBlocked && blockUntil && new Date() >= blockUntil) {
      setIsBlocked(false);
      setBlockUntil(null);
      setFailedAttempts(0);
    }

    // Check rate limiting based on failed attempts
    if (failedAttempts > 0 && lastAttemptTime) {
      const timeSinceLastAttempt = new Date().getTime() - lastAttemptTime.getTime();
      const minDelay = Math.min(failedAttempts * 2000, 30000); // 2s per attempt, max 30s
      
      if (timeSinceLastAttempt < minDelay) {
        const remainingDelay = Math.ceil((minDelay - timeSinceLastAttempt) / 1000);
        setError(`Poczekaj ${remainingDelay} sekund przed kolejną próbą.`);
        return;
      }
    }

    setError('');
    setValidating(true);
    
    try {
      const response = await publicAPI.validateToken(tokenToValidate, 'activation');
      setTokenValid(true);
      setUserData(response.user);
      setStep('consent');
      setFailedAttempts(0); // Reset on success
      setLastAttemptTime(null);
    } catch (error: any) {
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);
      setLastAttemptTime(new Date());
      
      // Handle different error types
      if (error.response?.status === 429) {
        // Server-side rate limiting
        setError('Zbyt wiele nieudanych prób aktywacji. Dostęp został tymczasowo ograniczony ze względów bezpieczeństwa. Skontaktuj się z lekarzem jeśli problem będzie się powtarzał.');
        setIsBlocked(true);
        setBlockUntil(new Date(Date.now() + 15 * 60 * 1000)); // 15 minutes
      } else if (error.response?.status === 403) {
        // IP blocked
        setError('Dostęp tymczasowo ograniczony ze względów bezpieczeństwa. Skontaktuj się z lekarzem jeśli problem będzie się powtarzał.');
        setIsBlocked(true);
        setBlockUntil(new Date(Date.now() + 30 * 60 * 1000)); // 30 minutes
      } else {
        // Regular validation error
        let errorMessage = 'Kod aktywacyjny jest nieprawidłowy lub już został użyty. Sprawdź czy wprowadziłeś wszystkie 8 cyfr poprawnie lub skontaktuj się z lekarzem.';
        
        if (newFailedAttempts >= 3) {
          errorMessage += ` (Nieudane próby: ${newFailedAttempts}/5)`;
        }
        
        setError(errorMessage);
        
        // Client-side temporary block after 5 attempts
        if (newFailedAttempts >= 5) {
          setIsBlocked(true);
          setBlockUntil(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes
        }
      }
      
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  };

  const handleManualTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (manualToken.length !== 8) {
      setError('Kod aktywacyjny musi składać się z dokładnie 8 cyfr');
      return;
    }
    
    // Check if blocked
    if (isBlocked && blockUntil && new Date() < blockUntil) {
      const remainingTime = Math.ceil((blockUntil.getTime() - new Date().getTime()) / 1000);
      setError(`Zbyt wiele nieudanych prób. Spróbuj ponownie za ${remainingTime} sekund.`);
      return;
    }
    
    await validateToken(manualToken);
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 8); // Only digits, max 8
    setManualToken(value);
    setError(''); // Clear error when typing
  };

  useEffect(() => {
    if (urlToken) {
      validateToken(urlToken);
    } else {
      setValidating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlToken]);

  const handleConsentSubmit = (data: any) => {
    setConsentData(data);
    setStep('password');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Hasła nie są identyczne');
      return;
    }

    if (password.length < 8) {
      setError('Hasło musi mieć co najmniej 8 znaków');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use urlToken or manualToken depending on activation mode
      const tokenToUse = activationMode === 'url' ? urlToken! : manualToken;
      await publicAPI.activateAccountWithConsent(tokenToUse, password, consentData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Konto zostało aktywowane. Możesz się teraz zalogować.' }
        });
      }, 3000);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Błąd podczas aktywacji konta');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.match(/[a-z]/)) score++;
    if (password.match(/[A-Z]/)) score++;
    if (password.match(/[0-9]/)) score++;
    if (password.match(/[^A-Za-z0-9]/)) score++;
    return score;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordCriteria({
      length: newPassword.length >= 8,
      lowercase: /[a-z]/.test(newPassword),
      uppercase: /[A-Z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[^A-Za-z0-9]/.test(newPassword),
    });
  };

  const getPasswordStrengthLabel = (score: number) => {
    switch (score) {
      case 0:
      case 1:
        return { label: 'Bardzo słabe', color: '#dc3545' };
      case 2:
        return { label: 'Słabe', color: '#fd7e14' };
      case 3:
        return { label: 'Średnie', color: '#ffc107' };
      case 4:
        return { label: 'Dobre', color: '#20c997' };
      case 5:
        return { label: 'Bardzo dobre', color: '#28a745' };
      default:
        return { label: '', color: '' };
    }
  };

  const passwordStrength = getPasswordStrength(password);
  const strengthInfo = getPasswordStrengthLabel(passwordStrength);

  if (validating) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{maxWidth: '420px', padding: '2rem', textAlign: 'center'}}>
          <FontAwesomeIcon icon={faSpinner} spin className="loading-icon" />
          <h2>Sprawdzanie tokenu...</h2>
          <p>Proszę czekać, weryfikujemy Twój link aktywacyjny.</p>
        </div>
      </div>
    );
  }

  // Manual token entry for elderly patients
  if (step === 'token' && activationMode === 'manual') {
    return (
      <div className="auth-container">
        <div className="auth-form-wrapper" style={{padding: '2rem'}}>
          <div className="auth-card">
            <div className="auth-header">
              <div className="brand-section">
                <img src="/logo-radonek.png" alt="Radonek Logo" className="auth-logo" />
                <div className="brand-info">
                  <h1>Radonek</h1>
                  <p className="brand-subtitle">Asystent pacjenta</p>
                </div>
              </div>
              <div className="form-title">
                <h2>Aktywacja konta</h2>
                <p className="auth-form-subtitle">Wpisz 8-cyfrowy kod aktywacyjny z zaproszenia.</p>
              </div>
            </div>
            
            <form onSubmit={handleManualTokenSubmit} className="auth-form">
              {error && (
                <div className="error-message">
                  <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="manualToken">Kod aktywacyjny</label>
                <input
                  id="manualToken"
                  type="text"
                  value={manualToken}
                  onChange={handleTokenChange}
                  placeholder="12345678"
                  maxLength={8}
                  pattern="[0-9]{8}"
                  required
                  disabled={loading || isBlocked}
                  style={{
                    fontSize: '1.5em',
                    textAlign: 'center',
                    letterSpacing: '0.2em',
                  }}
                />
              </div>

              <button 
                type="submit" 
                className="auth-button"
                disabled={loading || manualToken.length !== 8 || isBlocked}
              >
                {loading ? 'Sprawdzanie...' : 'Potwierdź kod'}
              </button>

              <div className="auth-links">
                <Link to="/login" className="auth-link">
                  Masz już konto? Zaloguj się
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid && activationMode === 'url') {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{maxWidth: '420px', padding: '2rem', textAlign: 'center'}}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
            <h2>Problem z kodem</h2>
            <p>{error}</p>
            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1.5rem'}}>
              <button 
                onClick={() => {
                  setActivationMode('manual');
                  setStep('token');
                  setError('');
                }}
                className="auth-button"
              >
                Wpisz kod ręcznie
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="auth-button secondary"
              >
                Powrót do logowania
              </button>
            </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card" style={{maxWidth: '420px', padding: '2rem', textAlign: 'center'}}>
          <FontAwesomeIcon icon={faCheck} className="success-icon" />
          <h2>Konto aktywowane!</h2>
          <p>Zaraz zostaniesz przekierowany do strony logowania.</p>
        </div>
      </div>
    );
  }

  // Render consent form step
  if (step === 'consent') {
    return (
      <div className="auth-container">
        <ConsentForm 
          userEmail={userData?.email || ''}
          userName={userData?.full_name || ''}
          onSubmit={handleConsentSubmit} 
          loading={loading} 
        />
      </div>
    );
  }

  // Render password setup step
  return (
    <div className="auth-container">
      <div className="auth-split-layout">
        <div className="auth-branding">
          <div className="brand-section">
            <img src="/logo-radonek.png" alt="Radonek Logo" className="auth-logo" />
            <div className="brand-info">
              <h1>Radonek</h1>
              <p className="brand-subtitle">Zgoda na udział w badaniu</p>
            </div>
          </div>
          <div className="about-section">
            <h3>Prospektywne obserwacyjne badanie naukowe</h3>
            <div className="key-features">
              <div className="feature-highlight">
                <div className="feature-icon">🔬</div>
                <div className="feature-content">
                  <h4>Cel badania</h4>
                  <p>Ocena użyteczności wirtualnego asystenta AI w edukacji i wsparciu pacjentów onkologicznych.</p>
                </div>
              </div>
              <div className="feature-highlight">
                <div className="feature-icon">🛡️</div>
                <div className="feature-content">
                  <h4>Ochrona danych</h4>
                  <p>Twoje dane są w pełni zanonimizowane i chronione zgodnie z RODO.</p>
                </div>
              </div>
              <div className="feature-highlight">
                <div className="feature-icon">✅</div>
                <div className="feature-content">
                  <h4>Jak to działa?</h4>
                  <p>Zadaj pytanie naszemu asystenowi. Asystent wykorzysta wiedzę na temat twojej choroby i dolegliwości, którą otrzymał od Twojego lekarza. Dzięki temu otrzymasz odpowiedzi dotyczące dokładnie Twojego przypadku.</p>
                  <p>Na zadane pytania otrzymasz dwie odpowiedzi: jedną wykorzystującą wiedzę, którą przygotowaliśmy specjalnie dla naszych pacjentów (RAG) i drugą od strandardowego modelu sztucznej inteligencji (ChatGPT 4o). Wybierz, która będzie bardziej pomocna. Odpowiedzi omówisz ze swoim lekarzem na wizycie. Udział jest dobrowolny i możesz go zakończyć w dowolnym momencie bez konsekwencji.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="auth-form-wrapper">
          <div className="auth-card">
            <div className="auth-header">
              <div className="form-title">
                <h2>Ustaw hasło</h2>
                <p className="auth-form-subtitle">Ostatni krok - zabezpiecz swoje konto.</p>
              </div>
            </div>
            
            <form onSubmit={handlePasswordSubmit} className="auth-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label htmlFor="password">Nowe hasło</label>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Wprowadź nowe hasło"
                  required
                  minLength={8}
                />
              </div>

              <div className="password-strength-container">
                <div className="password-strength-bar-container">
                  <div 
                    className="password-strength-bar" 
                    style={{ 
                      width: `${passwordStrength * 20}%`, 
                      backgroundColor: strengthInfo.color 
                    }}
                  />
                </div>
                {password.length > 0 && (
                  <div className="password-strength-label" style={{ color: strengthInfo.color }}>
                    Siła hasła: {strengthInfo.label}
                  </div>
                )}
              </div>

              <div className="password-criteria">
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#666' }}>
                  Wymagania dotyczące hasła:
                </h4>
                <div className={`criteria-item ${passwordCriteria.length ? 'valid' : 'invalid'}`}>
                  <FontAwesomeIcon 
                    icon={passwordCriteria.length ? faCheckCircle : faTimesCircle} 
                    className={passwordCriteria.length ? 'criteria-valid' : 'criteria-invalid'}
                  /> 
                  Co najmniej 8 znaków
                </div>
                <div className={`criteria-item ${passwordCriteria.lowercase ? 'valid' : 'invalid'}`}>
                  <FontAwesomeIcon 
                    icon={passwordCriteria.lowercase ? faCheckCircle : faTimesCircle} 
                    className={passwordCriteria.lowercase ? 'criteria-valid' : 'criteria-invalid'}
                  /> 
                  Mała litera (a-z)
                </div>
                <div className={`criteria-item ${passwordCriteria.uppercase ? 'valid' : 'invalid'}`}>
                  <FontAwesomeIcon 
                    icon={passwordCriteria.uppercase ? faCheckCircle : faTimesCircle} 
                    className={passwordCriteria.uppercase ? 'criteria-valid' : 'criteria-invalid'}
                  /> 
                  Wielka litera (A-Z)
                </div>
                <div className={`criteria-item ${passwordCriteria.number ? 'valid' : 'invalid'}`}>
                  <FontAwesomeIcon 
                    icon={passwordCriteria.number ? faCheckCircle : faTimesCircle} 
                    className={passwordCriteria.number ? 'criteria-valid' : 'criteria-invalid'}
                  /> 
                  Cyfra (0-9)
                </div>
                <div className={`criteria-item ${passwordCriteria.special ? 'valid' : 'invalid'}`}>
                  <FontAwesomeIcon 
                    icon={passwordCriteria.special ? faCheckCircle : faTimesCircle} 
                    className={passwordCriteria.special ? 'criteria-valid' : 'criteria-invalid'}
                  /> 
                  Znak specjalny (!@#$%^&*)
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Potwierdź hasło</label>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Potwierdź nowe hasło"
                  required
                  minLength={8}
                />
              </div>

              <button 
                type="submit" 
                className="auth-button"
                disabled={loading || password !== confirmPassword || password.length < 8 || passwordStrength < 3}
              >
                {loading ? 'Aktywowanie...' : 'Aktywuj konto'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};